/**
 * BuildFromPhotosDialog - "Build from photos", an AI action on a surface variant.
 *
 * The vendor drops whatever photos they have of the product (a sheet of tiles,
 * a single tile on white, a room). One vision call sorts them, then the seamless
 * generator's computer vision trims them, reads the pattern from the joints and
 * cuts the tiles (or the repeat cell) at the product's proportions. The result
 * goes straight into the variant's tile images, ready to generate.
 *
 * Costs credits (held when it starts, given back when it fails or finds nothing).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Check, CircleAlert, Coins, ImagePlus, Loader2, Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SizeAxis } from '../../../../../../common/models/product-v2.model';
import { getPhotoAnalysisQuote, type PhotoAnalysisQuote } from '../../../api/productV2Api';
import { ALL_PATTERNS } from '../../axes/PatternSelector';
import { useBuildFromPhotos } from './build-from-photos.store';
import { toCm } from '../../../../../../common/utils/units';

/** A size axis in cm (width, height), or undefined when it is not usable. */
export function sizeInCm(size?: SizeAxis | null): [number, number] | undefined {
  if (!size || !(size.width > 0) || !(size.height > 0)) return undefined;
  return [+toCm(size.width, size.unit).toFixed(2), +toCm(size.height, size.unit).toFixed(2)];
}

/** "Stack bond" / "stackbond" → "stackbond". */
export function patternKey(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase().replace(/[\s_-]+/g, '') : '';
}

export function patternLabel(value: string | null | undefined): string {
  if (!value) return '';
  return ALL_PATTERNS.find((p) => p.value === value)?.label ?? value;
}

export interface BuildFromPhotosApply {
  mode: 'tiles' | 'seamless';
  files: File[];
  pattern: string | null;
  /** A seamless cell's size in cm, when the product's size is known. */
  cellSizeCm?: number[] | null;
  /** The photos lay the tiles mirrored (stack bond's Mirror option). */
  mirror?: boolean;
}

interface BuildFromPhotosDialogProps {
  /** Whose run this is (the variant's uuid): the run outlives the dialog. */
  runKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The variant's size axis: tiles are cut at its proportions. */
  size?: SizeAxis | null;
  /** The variant's pattern, when chosen: it wins over what the photos suggest. */
  pattern?: string;
  /** The variant's colour: the glaze nearest it is offered ticked. */
  colorHex?: string;
  /** Photos already on the product (gallery), offered to pick from. */
  productPhotoUrls?: string[];
  /** Photos found for the product elsewhere (its page, by "Add product with AI"): offered first, ticked. */
  suggestedUrls?: string[];
  onApply: (result: BuildFromPhotosApply) => void;
}

async function urlToFile(url: string, name: string): Promise<File> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not load ${name}`);
  const blob = await response.blob();
  return new File([blob], name, { type: blob.type || 'image/jpeg' });
}

export const BuildFromPhotosDialog: React.FC<BuildFromPhotosDialogProps> = ({
  runKey,
  open,
  onOpenChange,
  size,
  pattern,
  colorHex,
  productPhotoUrls = [],
  suggestedUrls,
  onApply,
}) => {
  const { t } = useTranslation();
  const [quote, setQuote] = useState<PhotoAnalysisQuote | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [pickedUrls, setPickedUrls] = useState<string[]>([]);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const alive = useRef(true);

  const runState = useBuildFromPhotos((st) => st.runs[runKey]);
  const startRun = useBuildFromPhotos((st) => st.start);
  const resetRun = useBuildFromPhotos((st) => st.reset);
  const phase = runState?.phase ?? 'pick';
  const step = runState?.step ?? 'reading';
  const result = runState?.result ?? null;
  const error = runState?.error
    ? (runState.error === 'timeout' ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.TIMEOUT')
      : runState.error === 'failed' ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.FAILED') : runState.error)
    : null;

  const sizeCm = useMemo(() => sizeInCm(size), [size]);
  const currentPattern = patternKey(pattern);
  const maxPhotos = quote?.maxPhotos ?? 12;
  const count = files.length + pickedUrls.length;
  const photoUrls = useMemo(
    () => Array.from(new Set([...(suggestedUrls || []), ...productPhotoUrls])),
    [suggestedUrls, productPhotoUrls],
  );

  // Photos suggested for the product come ticked, as many as one run takes.
  useEffect(() => {
    if (suggestedUrls?.length) setPickedUrls(suggestedUrls.slice(0, maxPhotos));
  }, [suggestedUrls, maxPhotos]);
  const cost = quote?.credits ?? null;
  const short = quote != null && cost != null && quote.balance < cost;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    getPhotoAnalysisQuote().then((q) => alive.current && setQuote(q)).catch(() => undefined);
  }, [open, phase]);

  // A new result: tiles all chosen (one per glaze); seamless, the first (a variant takes one texture).
  useEffect(() => {
    if (!result) return;
    const offered = result.preselect?.filter((i) => i >= 0 && i < result.images.length);
    setChosen(new Set(result.mode === 'seamless' ? [0] : offered?.length ? offered : result.images.map((_, i) => i)));
  }, [result]);

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const onDrop = useCallback((accepted: File[]) => {
    setFiles((prev) => [...prev, ...accepted].slice(0, Math.max(0, maxPhotos - pickedUrls.length)));
  }, [maxPhotos, pickedUrls.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    disabled: phase !== 'pick',
  });

  const togglePicked = (url: string) => {
    setPickedUrls((prev) => (prev.includes(url)
      ? prev.filter((u) => u !== url)
      : count < maxPhotos ? [...prev, url] : prev));
  };

  const reset = () => {
    resetRun(runKey);
    setApplyError(null);
    setChosen(new Set());
  };

  const run = () => {
    setApplyError(null);
    startRun(runKey, {
      files,
      imageUrls: pickedUrls,
      sizeCm,
      pattern: currentPattern || undefined,
      colorHex: /^#?[0-9a-f]{6}$/i.test(colorHex || '') ? colorHex : undefined,
    });
  };

  const apply = async () => {
    if (!result || result.mode === 'none') return;
    setApplying(true);
    try {
      const picked = result.images.filter((_, i) => chosen.has(i));
      // Tiles cut to their outline come as PNG (transparent around the tile): keep the format.
      const made = await Promise.all(picked.map((im, i) =>
        urlToFile(im.url, `${result.mode === 'seamless' ? 'texture' : 'tile'}_${i + 1}.${/\.png(\?|$)/i.test(im.url) ? 'png' : 'jpg'}`)));
      onApply({
        mode: result.mode, files: made, pattern: result.pattern, cellSizeCm: result.cellSizeCm,
        mirror: !!result.options?.mirror,
      });
      setFiles([]);
      setPickedUrls([]);
      reset();
      onOpenChange(false);
    } catch (e: any) {
      setApplyError(e?.message || t('PAGES.NEW_PRODUCT.V3.PHOTOS.FAILED'));
    } finally {
      if (alive.current) setApplying(false);
    }
  };

  const kindLabel = (kind: string) => t(`PAGES.NEW_PRODUCT.V3.PHOTOS.KIND_${kind.toUpperCase()}`);
  const detected = result?.pattern ?? null;
  const differs = !!detected && !!currentPattern && detected !== currentPattern;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!applying) onOpenChange(o); }}>
      <DialogContent className="gap-0 p-0 sm:max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="border-b border-border px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <Sparkles className="size-[18px]" strokeWidth={1.5} />
            {t('PAGES.NEW_PRODUCT.V3.PHOTOS.TITLE')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V3.PHOTOS.DESCRIPTION')}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
          {phase === 'pick' && (
            <div className="flex flex-col gap-5">
              <div
                {...getRootProps()}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/40 px-6 py-8 text-center transition-colors',
                  isDragActive && 'border-foreground/40 bg-muted'
                )}
              >
                <input {...getInputProps()} data-testid="photos-input" />
                <ImagePlus className="size-6 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-sm text-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.DROP')}</p>
                <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.DROP_HINT', { max: maxPhotos })}</p>
              </div>

              {files.length > 0 && (
                <div className="grid grid-cols-6 gap-2">
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted">
                      <img src={previews[i]} alt={f.name} className="size-full object-cover" />
                      <button
                        type="button"
                        aria-label={t('COMMON.REMOVE_IMAGE')}
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute end-1 top-1 rounded-md bg-background/90 p-0.5 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {photoUrls.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-medium text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.FROM_PRODUCT')}</p>
                  <div className="grid grid-cols-6 gap-2">
                    {photoUrls.map((url) => {
                      const on = pickedUrls.includes(url);
                      return (
                        <button
                          key={url}
                          type="button"
                          aria-pressed={on}
                          onClick={() => togglePicked(url)}
                          className={cn(
                            'relative aspect-square overflow-hidden rounded-lg border bg-muted transition-shadow',
                            on ? 'border-foreground ring-[1.5px] ring-foreground' : 'border-border hover:border-foreground/30'
                          )}
                        >
                          <img src={url} alt="" referrerPolicy="no-referrer" className="size-full object-cover" />
                          {on && (
                            <span className="absolute end-1 top-1 rounded-md bg-foreground p-0.5 text-background">
                              <Check className="size-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {sizeCm
                  ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.SIZE_USED', { w: sizeCm[0], h: sizeCm[1] })
                  : t('PAGES.NEW_PRODUCT.V3.PHOTOS.SIZE_MISSING')}
                {currentPattern && ` ${t('PAGES.NEW_PRODUCT.V3.PHOTOS.PATTERN_USED', { pattern: patternLabel(currentPattern) })}`}
              </p>
            </div>
          )}

          {phase === 'running' && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-foreground">
                {step === 'reading' ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.STEP_READING') : t('PAGES.NEW_PRODUCT.V3.PHOTOS.STEP_CUTTING')}
              </p>
              <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.STEP_HINT')}</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <CircleAlert className="size-6 text-destructive" strokeWidth={1.5} />
              <p className="text-sm text-foreground">{error}</p>
              <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.REFUNDED')}</p>
            </div>
          )}

          {phase === 'result' && result && (
            <div className="flex flex-col gap-5">
              {result.mode === 'none' ? (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                  <p className="text-sm text-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.NOTHING_USABLE')}</p>
                  <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.REFUNDED')}</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    {detected && (
                      <span className="inline-flex items-center gap-2 rounded-lg border border-border px-2.5 py-1 text-sm">
                        {ALL_PATTERNS.find((p) => p.value === detected)?.icon && (
                          <img src={ALL_PATTERNS.find((p) => p.value === detected)!.icon} alt="" className="size-5" />
                        )}
                        {t('PAGES.NEW_PRODUCT.V3.PHOTOS.PATTERN', { pattern: patternLabel(detected) })}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {result.mode === 'seamless'
                        ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.MODE_SEAMLESS', { count: result.images.length })
                        : t('PAGES.NEW_PRODUCT.V3.PHOTOS.MODE_TILES', { count: result.images.length })}
                    </span>
                  </div>

                  {result.options?.mirror && (
                    <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.MIRROR_NOTE')}</p>
                  )}

                  {(differs || (!currentPattern && detected)) && (
                    <p className="flex items-start gap-1.5 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-xs text-warning">
                      <CircleAlert className="mt-px size-3.5 shrink-0" />
                      {differs
                        ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.PATTERN_DIFFERS', { current: patternLabel(currentPattern), detected: patternLabel(detected) })
                        : t('PAGES.NEW_PRODUCT.V3.PHOTOS.PATTERN_SET', { detected: patternLabel(detected) })}
                    </p>
                  )}

                  <div className="grid grid-cols-4 gap-3">
                    {result.images.map((im, i) => {
                      const on = chosen.has(i);
                      return (
                        <button
                          key={im.url}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setChosen((prev) => {
                            if (result.mode === 'seamless') return new Set([i]);
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={cn(
                            'relative flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/40 p-2 transition-shadow',
                            on ? 'border-foreground ring-[1.5px] ring-foreground' : 'border-border opacity-60 hover:opacity-100'
                          )}
                        >
                          <img src={im.url} alt="" className="max-h-full max-w-full object-contain" />
                          {on && (
                            <span className="absolute end-1.5 top-1.5 rounded-md bg-foreground p-0.5 text-background">
                              <Check className="size-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {result.photos.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.PER_PHOTO')}</p>
                  <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
                    {result.photos.map((p) => (
                      <li key={p.index} className="flex items-center gap-3 px-3 py-2 text-xs">
                        <span className="w-14 shrink-0 text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PHOTOS.PHOTO_N', { n: p.index + 1 })}</span>
                        <span className="w-32 shrink-0 text-foreground">{kindLabel(p.kind)}</span>
                        <span className={cn('min-w-0 flex-1 truncate', p.used ? 'text-foreground' : 'text-muted-foreground')} title={p.note}>
                          {p.used ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.USED') : t('PAGES.NEW_PRODUCT.V3.PHOTOS.SKIPPED')}
                          {p.note ? ` · ${p.note}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {applyError && <p className="text-xs text-destructive">{applyError}</p>}
            </div>
          )}
        </div>

        <DialogFooter className="mx-0 mb-0 flex items-center border-t border-border px-6 py-4 sm:justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Coins className="size-3.5" />
            {cost != null
              ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.COST', { cost, balance: quote?.balance ?? '—' })
              : '…'}
          </span>
          <div className="flex gap-2">
            {phase === 'pick' && (
              <>
                <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('COMMON.CANCEL')}</Button>
                <Button variant="outline" disabled={count === 0 || short} onClick={run}>
                  <Sparkles />
                  {short ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.NOT_ENOUGH') : t('PAGES.NEW_PRODUCT.V3.PHOTOS.RUN', { count })}
                </Button>
              </>
            )}
            {phase === 'running' && (
              <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('PAGES.NEW_PRODUCT.V3.PHOTOS.RUN_IN_BACKGROUND')}</Button>
            )}
            {(phase === 'error' || (phase === 'result' && result?.mode === 'none')) && (
              <Button variant="outline" onClick={reset}>{t('PAGES.NEW_PRODUCT.V3.PHOTOS.TRY_AGAIN')}</Button>
            )}
            {phase === 'result' && result && result.mode !== 'none' && (
              <>
                <Button variant="ghost" onClick={reset} disabled={applying}>{t('PAGES.NEW_PRODUCT.V3.PHOTOS.START_OVER')}</Button>
                <Button variant="outline" onClick={apply} disabled={applying || chosen.size === 0}>
                  {applying ? <Loader2 className="animate-spin" /> : <Check />}
                  {result.mode === 'seamless'
                    ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.USE_SEAMLESS')
                    : t('PAGES.NEW_PRODUCT.V3.PHOTOS.USE_TILES', { count: chosen.size })}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BuildFromPhotosDialog;
